import * as React from "react";
import { Text } from "@uikit";
import { Disclosure } from "@headlessui/react";

import { PlusIcon, MinusIcon } from "@heroicons/react/outline";
import { Course } from "@kalabo/golfsavers-common";
import {
  calcCrow,
  canWalkCourse,
  getCoursePricing,
  hasSingleGolferRate,
} from "@kalabo/golfsavers-common";
import dayjs from "dayjs";
import XE from "@components/common/xe";
import { useApp } from "@store/app";

interface IProps {
  course: Course;
}

const CourseFAQs = ({ course }: IProps) => {
  const date = useApp(($) => $.date);

  let has_non_playing_follower = course?.rates?.extras?.prices?.find((p) => {
    return p.type === "NON_PLAYING_FOLLOWER";
  });

  let faqs = React.useMemo(() => {
    let q: any = [];

    let cheapest_rate = getCoursePricing(null, course.golf_contracts);
    let has_single_rate = hasSingleGolferRate(date, course.golf_contracts);
    let can_walk = canWalkCourse(date, course.golf_contracts);
    let club_hire = course?.rates?.equipment?.prices?.find(
      (p) => p.type === "CLUB_HIRE"
    );

    // get center
    let center = course.region.points_of_reference?.find((p) => p.create_faq);
    let distance_to_center = calcCrow(
      course.latitude,
      course.longitude,
      Number(center?.latitude),
      Number(center?.longitude)
    );

    q.push({
      question: `How far is ${course.name?.en} from the center of ${course.region.name?.en}?`,
      answer: (
        <>
          {course.name?.en} is {distance_to_center.km.toFixed(1)} km from the
          center of {course.region.name?.en}.{" "}
          {course.full_address &&
            `The address is ${course.full_address}${
              course.location_description?.en
                ? ` (${course.location_description?.en})`
                : ""
            }.`}
        </>
      ),
    });

    if (cheapest_rate && cheapest_rate.price > 0) {
      q.push({
        question: `What is the green fee price at ${course.name?.en}?`,
        answer: (
          <>
            Green fees at {course.name?.en} start from{" "}
            <XE
              base_currency={cheapest_rate.currency}
              currency={cheapest_rate.currency}
              value={cheapest_rate.price}
            />{" "}
            for {cheapest_rate.product}. Please note that these prices are only
            valid until {dayjs(cheapest_rate.valid_to).format("DD MMMM YYYY")}.
            Additional charges may apply.
          </>
        ),
      });
    }

    if (course.policies.raincheck.enabled !== "UNKNOWN") {
      q.push({
        question: `What is the raincheck policy at ${course.name?.en}?`,
        answer:
          course.policies.raincheck.enabled === "YES" ? (
            <p
              className="hasList"
              dangerouslySetInnerHTML={{
                __html: course.policies.raincheck.config?.en,
              }}
            />
          ) : (
            `${course.name?.en} does not offer rain checks.`
          ),
      });
    }
    if (
      course?.facilities?.driving_range === "no" ||
      course?.facilities?.driving_range === "yes"
    ) {
      q.push({
        question: `Is there a driving range at ${course.name?.en}?`,
        answer:
          course?.facilities?.driving_range === "yes"
            ? `Yes, there is a driving range at ${course.name?.en}.`
            : `No, ${course.name?.en} does not have a driving range.`,
      });
    }

    if (course?.rates?.equipment?.enabled === "YES") {
      if (club_hire) {
        if (club_hire.to_price) {
          q.push({
            question: `Can you rent golf clubs at ${course.name?.en}?`,
            answer: (
              <>
                Yes, you can rent golf clubs at {course.name?.en}. Renting clubs
                costs between{" "}
                <XE
                  base_currency={club_hire.currency}
                  currency={club_hire.currency}
                  value={Number(club_hire.price)}
                />{" "}
                and{" "}
                <XE
                  base_currency={club_hire.currency}
                  currency={club_hire.currency}
                  value={Number(club_hire.to_price)}
                />{" "}
                for 18 holes.
              </>
            ),
          });
        } else {
          q.push({
            question: `Can you rent golf clubs at ${course.name?.en}?`,
            answer: (
              <>
                Yes, you can rent golf clubs at {course.name?.en}. Renting clubs
                costs{" "}
                <XE
                  base_currency={club_hire.currency}
                  currency={club_hire.currency}
                  value={Number(club_hire.price)}
                />{" "}
                for 18 holes.
              </>
            ),
          });
        }
      } else {
        q.push({
          question: `Can you rent golf clubs at ${course.name?.en}?`,
          answer: <>No, club hire is unavailable at {course.name?.en}.</>,
        });
      }
    }

    if (cheapest_rate) {
      if (has_single_rate) {
        q.push({
          question: `Are single golfers allowed at ${course.name?.en}?`,
          answer: (
            <>
              Yes, we currently have rates for single golfers at{" "}
              {course.name?.en}. Please note that single players may be grouped
              with other golfers and may have to wait until a suitable group
              tees off.
            </>
          ),
        });
      } else {
        q.push({
          question: `Are single golfers allowed at ${course.name?.en}?`,
          answer: (
            <>
              Unfortunately, we do not currently have rates for single golfers
              at {course.name?.en}.
            </>
          ),
        });
      }
    }

    if (can_walk) {
      q.push({
        question: `Can I walk the course at ${course.name?.en}?`,
        answer: (
          <>
            Golf carts are compulsory at {course.name?.en} so you cannot walk
            the course.
          </>
        ),
      });
    } else {
      q.push({
        question: `Can I walk the course at ${course.name?.en}?`,
        answer: (
          <>
            Yes. Golf carts are optional at {course.name?.en}, so you can choose
            to walk the course if you prefer.
          </>
        ),
      });
    }

    if (has_non_playing_follower) {
      if (Number(has_non_playing_follower.price) === 0) {
        q.push({
          question: `Can a non-golfer join me on my round at ${course.name?.en}?`,
          answer: (
            <>
              Yes, non-playing followers are allowed at {course.name?.en}. This
              course does not charge for non-playing followers. Non-golfers are
              expected to dress in an appropriate manner (i.e. correct golf
              attire).
            </>
          ),
        });
      } else {
        q.push({
          question: `Can a non-golfer join me on my round at ${course.name?.en}?`,
          answer: (
            <>
              Yes, non-playing followers are allowed at {course.name?.en}. The
              course charges a follower fee of{" "}
              <XE
                base_currency={has_non_playing_follower.currency}
                currency={has_non_playing_follower.currency}
                value={Number(has_non_playing_follower.price)}
              />
              {has_non_playing_follower.notes && (
                <> ({has_non_playing_follower.notes})</>
              )}
              . The follower fee must be paid directly to the pro shop.
              Non-golfers are expected to dress in an appropriate manner (i.e.
              correct golf attire).
            </>
          ),
        });
      }
    }

    return q;
  }, [course, date]);

  return (
    <div className="mb-9 border-t border-grey3 pt-10 flex flex-col">
      <Text
        element="h2"
        size={21}
        weight="semibold"
        className="mb-[30px]"
        color="black"
      >
        FAQ
      </Text>
      <dl>
        {faqs.map((faq: any) => (
          <Disclosure as="div" key={faq.question}>
            {({ open }) => (
              <div
                className={`${
                  open
                    ? "border-blue border-[2px]"
                    : "border border-bordergrey1"
                } bg-white rounded-lg  px-5 py-5 mb-2 box-border `}
              >
                <dt>
                  <Disclosure.Button className="text-left w-full flex justify-between items-center">
                    <Text
                      size={15}
                      weight="semibold"
                      className="mb-0"
                      element="h3"
                      leading={1.3}
                      color={"black80"}
                    >
                      {faq.question}
                    </Text>
                    <span className="ml-6 flex items-center">
                      {open ? (
                        <MinusIcon
                          className={"h-5 w-5 text-black"}
                          aria-hidden="true"
                        />
                      ) : (
                        <PlusIcon
                          className={"h-5 w-5 text-black"}
                          aria-hidden="true"
                        />
                      )}
                    </span>
                  </Disclosure.Button>
                </dt>
                <Disclosure.Panel as="dd" className="pr-12">
                  <Text
                    size={15}
                    leading={1.5}
                    color="black80"
                    className="mb-0 mt-4"
                  >
                    {faq.answer}
                  </Text>
                </Disclosure.Panel>
              </div>
            )}
          </Disclosure>
        ))}
      </dl>
    </div>
  );
};

export default CourseFAQs;
